import React, { useState } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';

// Components
import { QNavHeader } from '../components/layout/QNavHeader';
import { QNavSidebar } from '../components/layout/QNavSidebar';
import { QNavFooter } from '../components/layout/QNavFooter';
import { QNavChatInterface } from '../components/chat/QNavChatInterface';
import { QNavAnalytics } from '../components/analytics/QNavAnalytics';
import { QNavCustomerMetrics } from '../components/analytics/QNavCustomerMetrics';
import { QNavCustomerAnalyticsDashboard } from "../components/analytics/QNavCustomerAnalyticsDashboard";
import { QNavEngagementManagement } from '../components/risk/QNavEngagementManagement';
import QNavGovernance from '../components/governance/QNavGovernance';
import QNavCustomerOverview from '../components/customers/QNavCustomerOverview';
import QNavCustomerDetail from '../components/customers/QNavCustomerDetail';

// Context
import { AuthContext } from '../contexts/AuthContext';

// Types
interface QNavDashboardProps {
  signOut: () => void;
}

const QNavDashboard: React.FC<QNavDashboardProps> = ({ signOut }) => {
  const { user } = React.useContext(AuthContext) || { user: null };
  const navigate = useNavigate();

  // State
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);

  // Toggle sidebar
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Dashboard home component
  const DashboardHome = () => (
    <div className="h-full flex flex-col mb-10">
      <div className="bg-brand-gradient text-white p-6 rounded-qnav shadow-brand-card   m-8">
        <h1 className="text-3xl font-display font-bold mb-2">Welcome to Q-Navigator</h1>
        <p className="text-secondary-200 m-2">
          Your intelligent guide for Q enterprise adoption. Get personalized recommendations, track your
          progress, and accelerate your journey to success.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <div className="dropdown">
            <button
              className="qnav-btn-primary bg-brand-anchor hover:bg-primary-700 flex items-center"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <span>Start a Conversation</span>
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            <div className="dropdown-content">
              <button onClick={() => navigate('/dashboard/chat/csm')}>
                <span className="font-medium">Account CSM</span>
                <span className="block text-xs text-secondary-500 mt-1">Customer success management</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/chat/advisor')}
                className="opacity-50 cursor-not-allowed"
                disabled
              >
                <span className="font-medium">Service Specialist</span>
                <span className="block text-xs text-secondary-500 mt-1">Guide customers through adoption journey</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/chat/partner')}
                className="opacity-50 cursor-not-allowed"
                disabled
              >
                <span className="font-medium">Partner</span>
                <span className="block text-xs text-secondary-500 mt-1">Collaborate on customer solutions</span>
              </button>
              <button
                onClick={() => navigate('/dashboard/chat/tam')}
                className="opacity-50 cursor-not-allowed"
                disabled
              >
                <span className="font-medium">TAM/SA</span>
                <span className="block text-xs text-secondary-500 mt-1">Technical advisory and architecture</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6 mx-8">
        <div className="bg-surface-dark rounded-qnav p-6 border-l-4 border-secondary-500 shadow-sm transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px] flex flex-col">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mr-2">
              <svg
                className="w-5 h-5 text-secondary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                ></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-secondary-800">Customer Analytics</h2>
          </div>
          <p className="text-secondary-600 mb-4 flex-grow">Track transformation progress and generate executive-ready reports.</p>
          <button onClick={() => navigate('/dashboard/customer-analytics')} className="qnav-btn-secondary self-start">
            View Analytics
          </button>
        </div>

        <div className="bg-surface-blue rounded-qnav p-6 border-l-4 border-primary-600 shadow-sm transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px] flex flex-col">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-2">
              <svg
                className="w-5 h-5 text-primary-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                ></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-secondary-800">
              Kiro/Amazon Q Developer Learning Curriculum
            </h2>
          </div>
          <p className="text-secondary-600 mb-4 flex-grow">
            Access comprehensive Amazon Q Developer learning materials and curriculum resources.
          </p>
          <a 
            href="https://aws.highspot.com/items/68bef5a2823b09ba5b0a344d#1" 
            target="_blank" 
            rel="noopener noreferrer"
            className="qnav-btn-primary self-start"
          >
            Access Curriculum
          </a>
        </div>

        <div className="bg-surface-accent rounded-qnav p-6 border-l-4 border-accent-500 shadow-sm transition-all duration-300 hover:shadow-lg hover:translate-y-[-2px] flex flex-col">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mr-3">
              <svg
                className="w-5 h-5 text-accent-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-secondary-800">
              Engagement Management <span className="text-gray-500 font-medium">(Coming Soon)</span>
            </h2>
          </div>
          <p className="text-secondary-600 mb-4 flex-grow">
            Proactively identify program risks and get real-time mitigation guidance.
          </p>
          <button disabled className="qnav-btn-accent opacity-50 cursor-not-allowed self-start">
            Manage Engagement
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <QNavSidebar isOpen={sidebarOpen} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <QNavHeader toggleSidebar={toggleSidebar} sidebarOpen={sidebarOpen} user={user} />

        {/* Main content area - remove padding-top to eliminate gap */}
        <main className="flex-1 overflow-y-auto bg-background p-6 pt-0">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/chat" element={<Navigate to="/dashboard/chat/csm" replace />} />
            <Route path="/chat/advisor" element={<QNavChatInterface initialPersona="advisor" />} />
            <Route path="/chat/partner" element={<QNavChatInterface initialPersona="partner" />} />
            <Route path="/chat/tam" element={<QNavChatInterface initialPersona="tam" />} />
            <Route path="/chat/csm" element={<QNavChatInterface initialPersona="csm" />} />
            <Route path="/chat/:sessionId" element={<QNavChatInterface />} />
            <Route path="/analytics" element={<QNavAnalytics />} />
            <Route path="/customer-metrics" element={<QNavCustomerMetrics />} />
            <Route path="/customer-analytics" element={<QNavCustomerAnalyticsDashboard />} />
            <Route path="/engagement" element={<QNavEngagementManagement />} />
            <Route path="/governance" element={<QNavGovernance />} />
            <Route path="/customers" element={<QNavCustomerOverview />} />
            <Route path="/customers/:customerId" element={<QNavCustomerDetail />} />
          </Routes>
        </main>

        {/* Footer */}
        <QNavFooter signOut={signOut} />
      </div>
    </div>
  );
};

export default QNavDashboard;
